import { clsx } from 'clsx';
import { Field, Formik, FormikProps } from 'formik';
import { useEffect, useReducer, useRef } from 'react';
import {
  TemplateForm,
  TemplateFormState
} from '../../../../../template-form/interfaces/template-form';
import { Button } from '../../../../../button/Button';
import { getTemplateFormGridColumnSpan } from '../../../../../template-form/utils/templateFormGridUtil';
import type { FlatFileObjectConfigFormReducerType } from './reducer/interfaces';
import {
  FLAT_FILE_OBJECT_CONFIG_FORM_REDUCER_INITIAL_STATE,
  FlatFileObjectConfigFormReducer
} from './reducer/flatFileObjectConfigReducer';
import { FlatFileObjectConfigFormReducerActionsFactory } from './reducer/factory';
import { getErrorMessageFromAxiosError } from '../../../../../request/utils';
import { AsyncValidationForm } from '../../../../../shared/components/AsyncValidationForm/AsyncValidationForm';
import { useAsyncValidationForm } from '../../../../../shared/components/AsyncValidationForm/hooks/useAsyncValidationForm';
import { useInferSchema } from './hooks/useInferSchema';
import { InferSchemaSuccess } from './components/InferSchemaSuccess';
import { InferSchemaRunning } from './components/InferSchemaRunning';
import { Integration } from '../../../../../shared/interfaces/integration';
import { Banner } from '../../../../../banner/Banner';
import { FLAT_FILE_OBJECT_CONFIG_FORM_DATA_IDS } from './dataIds';
import { SourceAPI } from '../../../../../sources/api/SourceAPI';

interface FlatFileObjectConfigFormProps {
  form: TemplateForm;
  pipeline: Integration;
  objectId?: string;
  onInferSchemaSuccess: (objectId: string, formValues: unknown) => void;
}

export function FlatFileObjectConfigForm(props: FlatFileObjectConfigFormProps) {
  return (
    <AsyncValidationForm>
      <FlatFileObjectConfigFormInner {...props} />
    </AsyncValidationForm>
  );
}

function FlatFileObjectConfigFormInner({
  form,
  pipeline,
  objectId,
  onInferSchemaSuccess
}: FlatFileObjectConfigFormProps) {
  const formikRef = useRef<FormikProps<TemplateFormState>>(null);

  const [state, dispatch] = useReducer<FlatFileObjectConfigFormReducerType>(
    FlatFileObjectConfigFormReducer,
    FLAT_FILE_OBJECT_CONFIG_FORM_REDUCER_INITIAL_STATE
  );
  const { current: reducerActions } = useRef(
    FlatFileObjectConfigFormReducerActionsFactory(dispatch)
  );

  const { pollInferSchemaStatus } = useInferSchema(reducerActions);
  const { runValidationsAndSubmit, isValidating } = useAsyncValidationForm();

  useEffect(() => {
    if (objectId) {
      reducerActions.saveInferSchemaSuccess(objectId);
    }
  }, []);

  useEffect(() => {
    if (state.inferSchemaTaskId) {
      pollInferSchemaStatus(state.inferSchemaTaskId, pipeline.sourceId!);
    }
  }, [state.inferSchemaTaskId]);

  useEffect(() => {
    if (state.objectId) {
      onInferSchemaSuccess(state.objectId, formikRef.current!.values);
    }
  }, [state.objectId]);

  const fetchInferSchemaTaskId = () => {
    reducerActions.initInferSchema();

    const { name, ...values } = formikRef.current!.values;

    SourceAPI.triggerSourceObjectInferSchema(pipeline.sourceId!, {
      source_object_config: values
    })
      .then(res => {
        reducerActions.saveInferSchemaTaskId(res.data.task_request_id);
      })
      .catch(e => {
        const errorMessage = getErrorMessageFromAxiosError(e);
        reducerActions.saveInferSchemaError(errorMessage);
      });
  };

  const onInferSchemaClick = () => {
    runValidationsAndSubmit(
      formikRef.current! as FormikProps<unknown>,
      fetchInferSchemaTaskId,
      () => {}
    );
  };

  const inferObjectSchema = !state.objectId && !state.inferSchemaRunning;
  const disableFormEdit = state.inferSchemaRunning || state.objectId;
  const dataIds = FLAT_FILE_OBJECT_CONFIG_FORM_DATA_IDS;

  return (
    <Formik
      initialValues={form.initialValues}
      onSubmit={() => {}}
      validationSchema={form.validationSchema}
      innerRef={formikRef}
    >
      {formikProps => {
        return (
          <form noValidate onSubmit={() => formikProps.submitForm()} data-id={dataIds.form}>
            {form.template.map((grid, index) => (
              <>
                <div
                  className='d-grid grid-template-columns-20'
                  data-id={`${index}-${dataIds.template}`}
                >
                  {grid.fields.map(field => (
                    <div
                      key={field.props.name}
                      className={clsx(
                        field.rowBreak
                          ? 'd-grid grid-template-columns-20 grid-column-span-20'
                          : getTemplateFormGridColumnSpan(field.size)
                      )}
                    >
                      <div
                        className={
                          field.rowBreak ? getTemplateFormGridColumnSpan(field.size) : 'w-100'
                        }
                      >
                        <Field
                          {...field.props}
                          entity='OBJECTS'
                          formDisabled={disableFormEdit}
                          name={field.props.name}
                          key={field.props.name}
                          component={field.component}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className='mt-8'>
                  {state.objectId ? (
                    <InferSchemaSuccess
                      objectId={state.objectId}
                      pipeline={pipeline}
                      objectName={(formikRef.current?.values.object_name as string) ?? ''}
                    />
                  ) : null}

                  {state.inferSchemaRunning ? <InferSchemaRunning /> : null}

                  {inferObjectSchema ? (
                    <Button
                      dataId={dataIds.submitCTA}
                      onClick={onInferSchemaClick}
                      disabled={isValidating}
                      showProgress={isValidating}
                    >
                      Infer Object Schema
                    </Button>
                  ) : null}

                  {state.inferSchemaError ? (
                    <Banner
                      className='mt-5'
                      open
                      dataId={dataIds.errorBanner}
                      color='error'
                      icon='error-outline'
                    >
                      {state.inferSchemaError}
                    </Banner>
                  ) : null}
                </div>
              </>
            ))}
          </form>
        );
      }}
    </Formik>
  );
}
